import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  Users,
  UserCircle,
  Building2,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ApprovalStage, StageApprover, ApproverType } from '@/lib/api/types'

interface WorkflowStageEditorProps {
  stage: ApprovalStage
  isExpanded: boolean
  onToggleExpand: () => void
  onUpdate: (updates: Partial<ApprovalStage>) => void
  onDelete: () => void
  onAddApprover: () => void
  onRemoveApprover: (approverId: string) => void
  isDragging?: boolean
  dragHandleProps?: Record<string, unknown>
}

export function WorkflowStageEditor({
  stage,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onDelete,
  onAddApprover,
  onRemoveApprover,
  isDragging,
  dragHandleProps,
}: WorkflowStageEditorProps) {
  const [localName, setLocalName] = useState(stage.name)
  const [localDescription, setLocalDescription] = useState(stage.description || '')
  const [localRequiredApprovals, setLocalRequiredApprovals] = useState(stage.requiredApprovals)

  const handleNameBlur = () => {
    if (localName !== stage.name) {
      onUpdate({ name: localName })
    }
  }

  const handleDescriptionBlur = () => {
    if (localDescription !== stage.description) {
      onUpdate({ description: localDescription || undefined })
    }
  }

  const handleRequiredApprovalsBlur = () => {
    if (localRequiredApprovals !== stage.requiredApprovals) {
      onUpdate({ requiredApprovals: localRequiredApprovals })
    }
  }

  const getApproverIcon = (type: ApproverType) => {
    switch (type) {
      case 'User':
        return <UserCircle className="h-4 w-4" />
      case 'Role':
        return <Users className="h-4 w-4" />
      case 'Team':
        return <Building2 className="h-4 w-4" />
    }
  }

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        isDragging && 'shadow-lg ring-2 ring-neutral-200 dark:ring-neutral-700'
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <GripVertical className="h-5 w-5 text-neutral-400" />
          </div>

          {/* Stage Order Badge */}
          <Badge variant="secondary" className="h-6 w-6 rounded-full p-0 flex items-center justify-center">
            {stage.order}
          </Badge>

          {/* Stage Name */}
          <div className="flex-1">
            <CardTitle className="text-base">{stage.name}</CardTitle>
            {stage.description && !isExpanded && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                {stage.description}
              </p>
            )}
          </div>

          {/* Approvers Count */}
          <Badge variant="outline" className="text-xs">
            {stage.approvers.length} approver{stage.approvers.length !== 1 ? 's' : ''}
          </Badge>

          {/* Expand/Collapse Button */}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleExpand}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-2 space-y-4">
          {/* Stage Name Input */}
          <div className="space-y-2">
            <Label htmlFor={`stage-name-${stage.id}`}>Stage Name</Label>
            <Input
              id={`stage-name-${stage.id}`}
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="e.g., Manager Review"
            />
          </div>

          {/* Stage Description */}
          <div className="space-y-2">
            <Label htmlFor={`stage-description-${stage.id}`}>Description (optional)</Label>
            <Input
              id={`stage-description-${stage.id}`}
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onBlur={handleDescriptionBlur}
              placeholder="Describe what this stage is for..."
            />
          </div>

          {/* Required Approvals */}
          <div className="space-y-2">
            <Label htmlFor={`stage-required-${stage.id}`}>Required Approvals</Label>
            <Input
              id={`stage-required-${stage.id}`}
              type="number"
              min={1}
              max={stage.approvers.length || 1}
              value={localRequiredApprovals}
              onChange={(e) => setLocalRequiredApprovals(parseInt(e.target.value) || 1)}
              onBlur={handleRequiredApprovalsBlur}
              className="w-24"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Number of approvals needed to pass this stage
            </p>
          </div>

          {/* Toggle Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
              <div>
                <Label>Auto-approve minor changes</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Skip this stage for minor template edits
                </p>
              </div>
              <Switch
                checked={stage.autoApproveMinorChanges}
                onCheckedChange={(checked) => onUpdate({ autoApproveMinorChanges: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
              <div>
                <Label>Notify on submit</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Send notification when template reaches this stage
                </p>
              </div>
              <Switch
                checked={stage.notifyOnSubmit}
                onCheckedChange={(checked) => onUpdate({ notifyOnSubmit: checked })}
              />
            </div>
          </div>

          {/* Approvers List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Approvers</Label>
              <Button variant="outline" size="sm" onClick={onAddApprover}>
                <Plus className="h-4 w-4 mr-1" />
                Add Approver
              </Button>
            </div>

            {stage.approvers.length === 0 ? (
              <div className="p-4 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg text-center">
                <Users className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  No approvers added yet
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                  Add users, roles, or teams who can approve at this stage
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {stage.approvers.map((approver) => (
                  <ApproverItem
                    key={approver.id}
                    approver={approver}
                    onRemove={() => onRemoveApprover(approver.id)}
                    getApproverIcon={getApproverIcon}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

interface ApproverItemProps {
  approver: StageApprover
  onRemove: () => void
  getApproverIcon: (type: ApproverType) => React.ReactNode
}

function ApproverItem({ approver, onRemove, getApproverIcon }: ApproverItemProps) {
  return (
    <div className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
      <div className="p-2 rounded-full bg-neutral-200 dark:bg-neutral-700">
        {getApproverIcon(approver.approverType)}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
          {approver.approverName}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="secondary" className="text-xs">
            {approver.approverType}
          </Badge>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {[
              approver.canApprove && 'Approve',
              approver.canReject && 'Reject',
              approver.canRequestChanges && 'Request Changes',
            ]
              .filter(Boolean)
              .join(' / ')}
          </span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onRemove}
        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}
